/**
 * sl-9qm.6.1: ViewRenderer — Central render engine for AppBases
 *
 * Renders ViewComponents from ViewConfig with data from IViewDataSource.
 * Used in builder canvas (with selection/hover), internal mode, and public mode.
 *
 * SOLID:
 * - S: Only renders components (no config editing, no data fetching logic)
 * - O: New components are added via ComponentRegistry, not by modifying this file
 * - D: Depends on ComponentRegistry + ViewDataSourceFactory interfaces
 */
import {
  type AppBaseConfig,
  getMotionClassName,
  type SlotName,
} from '@streamlain/view-config';
import { useLiveData, useService } from '@toeverything/infra';
import {
  Component,
  createElement,
  type DragEvent as ReactDragEvent,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { APP_BASE_COMPONENT_ATTR } from '../../constants/app-base-css-contract';
import type { ViewComponentType } from '../../constants/view-component-types';
import {
  mapDatabaseRowsToBoundDataRows,
  mapDatabaseRowToBoundDataRow,
} from '../../services/bound-data-row';
import { ComponentRegistry } from '../../services/component-registry';
import { PublishEditorState } from '../../services/publish-editor-state';
import { ViewDataSourceFactory } from '../../services/view-datasource-factory';
import { ViewConfigStore } from '../../stores/view-config.store';
import type { InlineEditConfig } from '../../types/inline-edit.types';
import { DEFAULT_INLINE_EDIT_CONFIG } from '../../types/inline-edit.types';
import type {
  ViewRenderMode,
  ViewRuntimeContext,
} from '../../types/view-component.types';
import type {
  ViewComponentInstance,
  ViewConfig,
  ViewConfigSettings,
} from '../../types/view-config.types';
import type {
  DatabaseViewData,
  IViewDataSource,
  Row,
} from '../../types/view-datasource.types';
import { BuilderComponentWrapper } from '../view-components/builder-wrapper';
import { useEnsureBuiltInViewComponentsRegistered } from '../view-components/ensure-built-in-components';
import { ComponentErrorPlaceholder } from './component-error-placeholder';
import {
  createSharedViewRenderPlan,
  resolveRuntimeCustomProperties,
  selectActiveViewComponents,
} from './render-kernel';
import * as styles from './styles.css';
import { UnknownComponentPlaceholder } from './unknown-component-placeholder';
import { ViewNavigationStateProvider } from './view-navigation-state';

// ─── Error Boundary (Class Component — React requirement) ─────────────

interface ErrorBoundaryProps {
  componentType: string;
  mode: ViewRenderMode;
  onRemove?: () => void;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ViewComponentErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override render(): ReactNode {
    if (this.state.error) {
      return createElement(ComponentErrorPlaceholder, {
        componentType: this.props.componentType,
        error: this.state.error,
        mode: this.props.mode,
        onRemove: this.props.onRemove,
      });
    }
    return this.props.children;
  }
}

// ─── Single Component Renderer ────────────────────────────────────────

interface ComponentSlotProps {
  instance: ViewComponentInstance;
  mode: ViewRenderMode;
  selectedId?: string;
  hoveredId?: string;
  onSelect?: (id: string) => void;
  onHover?: (id: string | null) => void;
  onRemove?: (id: string) => void;
  /** sl-9qm.14.2: Inline edit config from ViewConfig settings */
  inlineEditConfig: InlineEditConfig;
  /** Live AppBaseConfig passed by the AppBase Builder preview path. */
  appBaseConfig?: AppBaseConfig;
  /** Workspace id used to resolve local workspace blob attachments in previews. */
  workspaceId?: string;
  /** Resolved branding payload from AppBase materialization. */
  appBaseBranding?: Record<string, unknown>;
  /** Read-only related sources already loaded by the Builder preview owner. */
  runtimeSources?: Record<string, unknown>;
  /** Editor/public-preview route state for dynamic routes such as `/:slug`. */
  previewRoute?: ViewRendererRouteState | null;
  /** Locale selected by the preview owner. */
  previewLocale?: string;
  /** Changes the preview-owned locale without navigating the browser. */
  onPreviewLocaleChange?: (locale: string) => void;
  /** View settings used to build the same runtimeContext shape as public. */
  viewSettings: ViewConfigSettings;
  viewData: DatabaseViewData | null;
  dataSourceRef: IViewDataSource | null;
  motionIndex: number;
  /**
   * sl-bpu6.6.2: Optional drag-start handler for builder-mode slots. When
   * provided, the slot becomes `draggable` and the handler runs on dragstart.
   * Consumers (e.g. TreeCanvas) write CanvasPayload into dataTransfer.
   */
  onDragStart?: (e: ReactDragEvent, instanceId: string) => void;
  /**
   * sl-pbux.8.4: optional map from `stylePresetId` (string) to the scoped
   * className that `renderStyleCss` emits. When the instance carries
   * `props.stylePresetId`, the slot wrapper picks up the matching class so
   * preset + variant rules apply.
   */
  styleClassMap?: Record<string, string>;
}

/**
 * Renders a single ViewComponent with data binding and error boundary.
 */
const ComponentSlot: FC<ComponentSlotProps> = ({
  instance,
  mode,
  selectedId,
  hoveredId,
  onSelect,
  onHover: _onHover,
  onRemove,
  inlineEditConfig,
  appBaseConfig,
  workspaceId,
  appBaseBranding,
  runtimeSources,
  previewRoute,
  previewLocale,
  onPreviewLocaleChange,
  onDragStart,
  styleClassMap,
  viewSettings,
  viewData,
  dataSourceRef,
  motionIndex,
}) => {
  const registry = useService(ComponentRegistry);

  const componentDef = registry.get(
    instance.componentType as ViewComponentType
  );

  if (!componentDef) {
    return createElement(UnknownComponentPlaceholder, {
      componentType: instance.componentType,
    });
  }

  const effectiveViewData = viewData;
  const data = resolveRuntimeDataRows(
    instance,
    effectiveViewData,
    previewRoute?.routeParams
  );
  const runtimeContext = createViewRuntimeContext({
    instance,
    appBaseConfig,
    workspaceId,
    appBaseBranding,
    runtimeSources,
    viewData: effectiveViewData,
    viewSettings,
    previewRoute,
    previewLocale,
    onPreviewLocaleChange,
  });
  const formulaData = createRuntimeFormulaData(runtimeContext, instance);

  const isSelected = selectedId === instance.id;
  const isHovered = hoveredId === instance.id;

  // sl-bpu6.7: merge defaultConfig under instance.props so that clearing a
  // prop in the Inspector (which deletes the key) reverts the component to
  // its declared default instead of crashing on `undefined`. Store-side we
  // already strip undefined keys — here we restore the baseline.
  const resolvedConfig = {
    ...(componentDef.defaultConfig ?? {}),
    ...instance.props,
  };

  const content = componentDef.render({
    config: resolvedConfig,
    data: mapDatabaseRowsToBoundDataRows(
      data,
      effectiveViewData?.columns ?? []
    ),
    mode,
    selected: isSelected,
    hovered: isHovered,
    onSelect: onSelect ? () => onSelect(instance.id) : undefined,
    // sl-9qm.14.2: Pass inline edit props
    rows: data,
    columns: effectiveViewData?.columns,
    dataSource: dataSourceRef ?? undefined,
    dataBindingConfig: instance.dataBinding,
    runtimeContext,
    inlineEditConfig,
  });

  const handleRemove = onRemove ? () => onRemove(instance.id) : undefined;

  const wrapped =
    mode === 'builder'
      ? createElement(BuilderComponentWrapper, {
          selected: isSelected,
          hovered: isHovered,
          onSelect: () => onSelect?.(instance.id),
          runtimeIndicator: !!(
            instance.condition ||
            instance.repeat ||
            instance.repeatKey
          ),
          onDragStart:
            onDragStart !== undefined
              ? (e: ReactDragEvent) => onDragStart(e, instance.id)
              : undefined,
          children: content,
        })
      : content;

  // `data-sl-instance-id` stays on the slot wrapper so callers (TreeCanvas,
  // tests) can locate slots by id regardless of render mode. The actual
  // drag source is scoped to the builder-wrapper drag handle — making the
  // whole slot `draggable` would hijack text selection + nested input/link
  // interaction.
  const stylePresetId =
    typeof instance.props['stylePresetId'] === 'string'
      ? (instance.props['stylePresetId'] as string)
      : null;
  const motionId =
    typeof instance.props['motionId'] === 'string'
      ? (instance.props['motionId'] as string)
      : null;
  const presetClass =
    stylePresetId && styleClassMap ? styleClassMap[stylePresetId] : null;
  const motionClass = motionId ? getMotionClassName({ id: motionId }) : null;
  const slotClassName = [styles.componentSlot, motionClass, presetClass]
    .filter((value): value is string => Boolean(value))
    .join(' ');
  return createElement(
    'div',
    {
      className: slotClassName,
      style: {
        position: 'relative',
        '--sl-app-motion-index': motionIndex,
        ...resolveRuntimeCustomProperties(instance, formulaData),
      },
      [APP_BASE_COMPONENT_ATTR]: instance.componentType,
      'data-sl-instance-id': instance.id,
    },
    createElement(ViewComponentErrorBoundary, {
      componentType: instance.componentType,
      mode,
      onRemove: handleRemove,
      children: wrapped,
    })
  );
};

// ─── ViewRenderer ─────────────────────────────────────────────────────

interface ViewRendererProps {
  /** ViewConfig from Y.js store */
  config: ViewConfig;
  /** Rendering mode */
  mode: ViewRenderMode;
  /** Active AppBaseConfig page. When set, page-owned components are scoped to it. */
  activePageId?: string | null;
  /** Editor/public-preview route state for dynamic routes such as `/:slug`. */
  previewRoute?: ViewRendererRouteState | null;
  /** Locale selected by the preview owner. */
  previewLocale?: string;
  /** Changes the preview-owned locale without navigating the browser. */
  onPreviewLocaleChange?: (locale: string) => void;
  /**
   * sl-bpu6.6.2: Optional drag-start handler. In builder mode, makes each slot
   * draggable and lets the caller write dataTransfer payload (e.g. CanvasPayload
   * for reorder). Ignored in non-builder modes.
   */
  onSlotDragStart?: (e: ReactDragEvent, instanceId: string) => void;
  /**
   * sl-pbux.8.4: optional preset-id → className map produced by
   * `renderStyleCss`. Passed down to ComponentSlot so each slot picks up
   * the preset class that matches `instance.props.stylePresetId`. When
   * absent, slots render without preset styling.
   */
  styleClassMap?: Record<string, string>;
  appBaseConfig?: AppBaseConfig;
  /** Workspace id used to resolve local workspace blob attachments in previews. */
  workspaceId?: string;
  /**
   * Resolved branding payload mirroring the materialized
   * AppBase branding shape (`{ settings: { displayName, ... }, logoUrl }`)
   * so editor previews render the same brand link as the AppBase Viewer.
   */
  appBaseBranding?: Record<string, unknown>;
  /** Read-only related sources already loaded by the Builder preview owner. */
  runtimeSources?: Record<string, unknown>;
}

function readRuntimeSourceViewData(value: unknown): DatabaseViewData | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const source = value as Partial<DatabaseViewData>;
  if (!Array.isArray(source.columns) || !Array.isArray(source.rows))
    return null;
  return {
    columns: source.columns,
    rows: source.rows,
    totalCount:
      typeof source.totalCount === 'number'
        ? source.totalCount
        : source.rows.length,
    writable: false,
  };
}

export interface ViewRendererRouteState {
  pageId: string;
  path: string;
  routeParams: Record<string, string>;
}

/**
 * Central renderer for AppBases.
 * Iterates over ViewConfig.components and renders each via ComponentSlot.
 */
export const ViewRenderer: FC<ViewRendererProps> = ({
  activePageId,
  config,
  mode,
  appBaseConfig,
  workspaceId,
  appBaseBranding,
  runtimeSources,
  previewRoute,
  previewLocale,
  onPreviewLocaleChange,
  onSlotDragStart,
  styleClassMap,
}) => {
  const registry = useService(ComponentRegistry);
  const factory = useService(ViewDataSourceFactory);
  useEnsureBuiltInViewComponentsRegistered(registry);
  const editorState = useService(PublishEditorState);
  const viewConfigStore = useService(ViewConfigStore);

  // sl-bpu6.6.3: PublishEditorState is the single source of truth for
  // builder-mode selection + hover. Driving local React state in parallel
  // (as before) caused divergence when an outside consumer — ESC handler,
  // Inspector, delete flow — cleared PublishEditorState without notifying
  // this component.
  const selectedId = useLiveData(editorState.selectedComponentId$) ?? undefined;
  const hoveredId = useLiveData(editorState.hoveredComponentId$);

  const handleSelect = useCallback(
    (id: string) => {
      editorState.select(id);
    },
    [editorState]
  );

  const handleHover = useCallback(
    (id: string | null) => {
      editorState.hover(id);
    },
    [editorState]
  );

  const handleRemove = useCallback(
    (id: string) => {
      viewConfigStore.removeComponent(id);
      if (editorState.selectedComponentId$.value === id) {
        editorState.select(null);
      }
    },
    [viewConfigStore, editorState]
  );

  // Components from config (sorted by position for consistent ordering)
  const effectiveActivePageId = previewRoute?.pageId ?? activePageId;
  const activeComponents = useMemo(
    () => selectActiveViewComponents(config.components, effectiveActivePageId),
    [config.components, effectiveActivePageId]
  );
  const [bindingState, setBindingState] = useState<ViewBindingState>({});
  useEffect(() => {
    const activeBindings = activeComponents.flatMap(instance => {
      if (!instance.dataBinding?.databaseDocId) return [];
      if (
        mode === 'preview' &&
        readRuntimeSourceViewData(
          runtimeSources?.[instance.dataBinding.sourceKey ?? 'content']
        )
      ) {
        return [];
      }
      const source = factory.create({
        sourceType: 'database',
        mode,
        snapshot: undefined,
      });
      return [
        {
          instance,
          source,
          bindingKey: createViewBindingKey(instance.dataBinding),
        },
      ];
    });
    setBindingState(current =>
      reconcileViewBindingState(current, activeBindings)
    );
    const subscriptions = activeBindings.map(
      ({ instance, source, bindingKey }) =>
        source.getViewData(instance.dataBinding!).subscribe(viewData => {
          setBindingState(current => {
            const existing = current[instance.id];
            if (!existing || existing.bindingKey !== bindingKey) return current;
            return {
              ...current,
              [instance.id]: { bindingKey, viewData, source },
            };
          });
        })
    );
    return () =>
      subscriptions.forEach(subscription => subscription.unsubscribe());
  }, [activeComponents, factory, mode, runtimeSources]);

  // sl-9qm.14.2: Resolve inline edit config from ViewConfig settings
  const inlineEditConfig =
    config.settings.inlineEdit ?? DEFAULT_INLINE_EDIT_CONFIG;
  const plan = useMemo(
    () =>
      createSharedViewRenderPlan({
        components: config.components,
        settings: config.settings,
        activePageId: effectiveActivePageId,
        mode,
        resolveFormulaData: (instance, originId) => {
          const viewData = resolvePlannedViewData(
            instance,
            originId,
            bindingState,
            runtimeSources,
            mode
          );
          return createRuntimeFormulaData(
            createViewRuntimeContext({
              instance,
              appBaseConfig,
              workspaceId,
              appBaseBranding,
              runtimeSources,
              viewData,
              viewSettings: config.settings,
              previewRoute,
              previewLocale,
              onPreviewLocaleChange,
            }),
            instance
          );
        },
      }),
    [
      config.settings,
      bindingState,
      config.components,
      appBaseBranding,
      appBaseConfig,
      effectiveActivePageId,
      mode,
      onPreviewLocaleChange,
      previewLocale,
      previewRoute,
      runtimeSources,
      workspaceId,
    ]
  );
  const { componentSlots, effectiveShape, layoutContract } = plan;

  return createElement(
    ViewNavigationStateProvider,
    { key: config.id },
    createElement(
      'div',
      { className: styles.viewRenderer },
      createElement(
        'div',
        {
          className: styles.shapeRoot,
          'data-shape': effectiveShape,
        },
        ...layoutContract.slots.map(slotName =>
          createElement(
            slotTagForSlot(slotName),
            {
              key: slotName,
              className: `${styles.pageSlot} ${slotClassForSlot(slotName)}`,
              'data-slot': slotName,
            },
            ...componentSlots[slotName].flatMap(entry => {
              const currentBinding = resolveCurrentBindingStateEntry(
                entry.instance,
                entry.originId,
                bindingState
              );
              const viewData = resolvePlannedViewData(
                entry.instance,
                entry.originId,
                bindingState,
                runtimeSources,
                mode
              );
              return [
                createElement(ComponentSlot, {
                  key: entry.key,
                  instance: entry.instance,
                  mode,
                  selectedId,
                  hoveredId: hoveredId ?? undefined,
                  onSelect: mode === 'builder' ? handleSelect : undefined,
                  onHover: mode === 'builder' ? handleHover : undefined,
                  onRemove: mode === 'builder' ? handleRemove : undefined,
                  inlineEditConfig,
                  appBaseConfig,
                  workspaceId,
                  appBaseBranding,
                  runtimeSources,
                  previewRoute,
                  previewLocale,
                  onPreviewLocaleChange,
                  onDragStart: mode === 'builder' ? onSlotDragStart : undefined,
                  styleClassMap,
                  viewSettings: config.settings,
                  viewData,
                  dataSourceRef: currentBinding?.source ?? null,
                  motionIndex: entry.motionIndex,
                }),
              ];
            })
          )
        )
      )
    )
  );
};

function slotTagForSlot(slotName: SlotName): string {
  if (slotName === 'header') return 'header';
  if (slotName === 'sidebar') return 'aside';
  if (slotName === 'main') return 'main';
  return 'footer';
}

function slotClassForSlot(slotName: SlotName): string {
  if (slotName === 'header') return styles.slotHeader;
  if (slotName === 'sidebar') return styles.slotSidebar;
  if (slotName === 'main') return styles.slotMain;
  return styles.slotFooter;
}

export function resolvePlannedViewData(
  instance: ViewComponentInstance,
  originId: string,
  bindingState: ViewBindingState,
  runtimeSources: Record<string, unknown> | undefined,
  mode: ViewRenderMode
): DatabaseViewData | null {
  const localized =
    mode === 'preview'
      ? readRuntimeSourceViewData(
          runtimeSources?.[instance.dataBinding?.sourceKey ?? 'content']
        )
      : null;
  if (localized) return localized;
  if (!instance.dataBinding) return null;
  const current = resolveCurrentBindingStateEntry(
    instance,
    originId,
    bindingState
  );
  if (current) return current.viewData;
  return EMPTY_VIEW_DATA;
}

interface ViewBindingStateEntry {
  bindingKey: string;
  viewData: DatabaseViewData;
  source: IViewDataSource;
}

type ViewBindingState = Record<string, ViewBindingStateEntry>;

const EMPTY_VIEW_DATA: DatabaseViewData = {
  columns: [],
  rows: [],
  totalCount: 0,
  writable: false,
};

function createViewBindingKey(
  binding: NonNullable<ViewComponentInstance['dataBinding']>
): string {
  return JSON.stringify(binding);
}

export function resolveCurrentBindingStateEntry(
  instance: ViewComponentInstance,
  originId: string,
  bindingState: ViewBindingState
): ViewBindingStateEntry | null {
  if (!instance.dataBinding) return null;
  const current = bindingState[originId];
  return current?.bindingKey === createViewBindingKey(instance.dataBinding)
    ? current
    : null;
}

export function reconcileViewBindingState(
  current: ViewBindingState,
  activeBindings: readonly {
    instance: ViewComponentInstance;
    source: IViewDataSource;
    bindingKey: string;
  }[]
): ViewBindingState {
  return Object.fromEntries(
    activeBindings.map(({ instance, source, bindingKey }) => {
      const existing = current[instance.id];
      return [
        instance.id,
        existing?.bindingKey === bindingKey
          ? { ...existing, source }
          : { bindingKey, viewData: EMPTY_VIEW_DATA, source },
      ];
    })
  );
}

export function createViewRuntimeContext({
  instance,
  appBaseConfig,
  workspaceId,
  appBaseBranding,
  runtimeSources,
  viewData,
  viewSettings,
  previewRoute,
  previewLocale,
  onPreviewLocaleChange,
}: {
  instance: ViewComponentInstance;
  appBaseConfig?: AppBaseConfig;
  workspaceId?: string;
  appBaseBranding?: Record<string, unknown>;
  runtimeSources?: Record<string, unknown>;
  viewData: DatabaseViewData | null;
  viewSettings: ViewConfigSettings;
  previewRoute?: ViewRendererRouteState | null;
  previewLocale?: string;
  onPreviewLocaleChange?: (locale: string) => void;
}): ViewRuntimeContext {
  const sourceKey = instance.dataBinding?.sourceKey ?? 'content';
  const row = resolveRuntimeRow(instance, viewData, previewRoute?.routeParams);
  const scopedRows = resolveRuntimeDataRows(
    instance,
    viewData,
    previewRoute?.routeParams
  );
  const source = viewData
    ? {
        source: { key: sourceKey },
        title: sourceKey,
        columns: viewData.columns,
        rows: scopedRows,
        totalCount:
          instance.dataBinding?.contextScope === 'row'
            ? scopedRows.length
            : viewData.totalCount,
        writable: viewData.writable,
      }
    : null;
  const routeParams: Record<string, string> = {
    ...(previewRoute?.routeParams ?? {}),
    ...(instance.dataBinding?.contextScope === 'row' && row
      ? { id: row.id, rowId: row.id }
      : {}),
  };
  const pageId =
    typeof instance.props['pageId'] === 'string'
      ? instance.props['pageId']
      : (previewRoute?.pageId ?? null);
  const page = pageId
    ? (appBaseConfig?.pages.find(entry => entry.id === pageId) ?? null)
    : null;
  return {
    view: viewSettings as unknown as Record<string, unknown>,
    appBase: appBaseConfig
      ? {
          config: appBaseConfig,
          defaultLocale: appBaseConfig.defaultLocale,
          availableLocales: appBaseConfig.availableLocales,
          ...(workspaceId ? { workspaceId } : {}),
          ...(appBaseBranding ? { branding: appBaseBranding } : {}),
        }
      : null,
    page: page ? (page as unknown as Record<string, unknown>) : null,
    row,
    source,
    sources: {
      ...(runtimeSources ?? {}),
      ...(source ? { [sourceKey]: source } : {}),
    },
    locale: previewLocale ?? appBaseConfig?.defaultLocale ?? 'en',
    ...(onPreviewLocaleChange ? { onLocaleChange: onPreviewLocaleChange } : {}),
    routeParams,
    component: instance.props,
  };
}

export function createRuntimeFormulaData(
  context: ViewRuntimeContext,
  instance: ViewComponentInstance
): Record<string, unknown> {
  const alias = instance.dataBinding?.sourceAlias;
  if (!alias) return context as unknown as Record<string, unknown>;
  return {
    ...context,
    [alias]: context.source,
  };
}

function resolveRuntimeRow(
  instance: ViewComponentInstance,
  viewData: DatabaseViewData | null,
  routeParams?: Record<string, string>
): Row | null {
  const runtimeRowId =
    typeof instance.props['__slRuntimeRowId'] === 'string'
      ? instance.props['__slRuntimeRowId']
      : null;
  const targetRowId =
    runtimeRowId ?? routeParams?.['rowId'] ?? routeParams?.['id'];
  if (targetRowId) {
    return viewData?.rows.find(row => row.id === targetRowId) ?? null;
  }
  const slug = routeParams?.['slug'];
  if (slug) {
    return (
      viewData?.rows.find(row => readRowSlug(row, viewData.columns) === slug) ??
      null
    );
  }
  return viewData?.rows[0] ?? null;
}

export function resolveRuntimeDataRows(
  instance: ViewComponentInstance,
  viewData: DatabaseViewData | null,
  routeParams?: Record<string, string>
): Row[] {
  const rows = viewData?.rows ?? [];
  if (instance.dataBinding?.contextScope !== 'row') return rows;
  const row = resolveRuntimeRow(instance, viewData, routeParams);
  return row ? rows.filter(candidate => candidate.id === row.id) : [];
}

function readRowSlug(
  row: Row,
  columns: DatabaseViewData['columns']
): string | null {
  const value = mapDatabaseRowToBoundDataRow(row, columns)['slug'];
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
}
